/**
 * Push a semantic event to the GTM dataLayer.
 *
 * Google Ads / GA4 conversion tags are bound to these events inside the GTM
 * container (GTM-WFL4QQ46) — the app only emits the intent, the tag config
 * lives in GTM so marketing can manage conversion IDs without code deploys.
 *
 * Rules:
 *  - Never include PII (name/email/phone) — GA4 policy + privacy.
 *  - Fire conversion-grade events only AFTER the underlying action truly
 *    succeeds (verified payment, successful submit), never on page load.
 *  - Always safe: a missing/blocked dataLayer must never break the UX path.
 */
export function trackEvent(event: string, params: Record<string, unknown> = {}): void {
  try {
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...params });
  } catch {
    /* dataLayer push must never throw into the UI */
  }
}

/**
 * Fire a GA4 event directly via gtag.js (property G-QWD5J1K9QC, loaded in
 * index.html). Use this for conversion events that must land in GA4 without
 * depending on a GTM container/trigger — e.g. `demo_booked`. Safe no-op if
 * gtag isn't on the page yet.
 */
export function trackGtagEvent(event: string, params: Record<string, unknown> = {}): void {
  try {
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag === "function") {
      w.gtag("event", event, params);
    }
  } catch {
    /* a tracking call must never break the UX path */
  }
}
